/**
 * SportNav Component
 * Collapsible sport navigation with logos and sub-league dropdowns
 * Created: 2025-12-23 16:15 EST
 * Updated: 2025-12-23 - Added Live and Today buttons
 */

import { useState, useRef, useEffect, useMemo } from 'react'
import { useOddsStore } from '../../stores/oddsStore'
import './SportNav.css'

// League logos
const LEAGUE_LOGOS = {
  NFL: '/logos/leagues/NFL.svg',
  NBA: '/logos/leagues/NBA.png',
  NHL: '/logos/leagues/NHL.png',
  MLB: '/logos/leagues/MLB.png',
}

// Sports configuration
const SPORTS = [
  { name: 'NFL', logo: LEAGUE_LOGOS.NFL },
  { name: 'CFB', icon: 'fas fa-football-ball' },
  { name: 'NBA', logo: LEAGUE_LOGOS.NBA },
  { name: 'CBB', icon: 'fas fa-basketball-ball' },
  { name: 'NHL', logo: LEAGUE_LOGOS.NHL },
  {
    name: 'Hockey+',
    icon: 'fas fa-hockey-puck',
    subLeagues: [
      { name: 'AHL', label: 'AHL', icon: 'fas fa-hockey-puck' },
      { name: 'PWHL', label: 'PWHL', icon: 'fas fa-hockey-puck' },
      { name: 'NCAAH', label: 'College Hockey', icon: 'fas fa-hockey-puck' }
    ]
  },
  { name: 'MLB', logo: LEAGUE_LOGOS.MLB },
  {
    name: 'Esports',
    icon: 'fas fa-gamepad',
    subLeagues: [
      { name: 'ESPORTS', label: 'All Esports', icon: 'fas fa-gamepad' },
      { name: 'NBA2K', label: 'NBA 2K', icon: 'fas fa-basketball-ball' },
      { name: 'MADDEN', label: 'Madden', icon: 'fas fa-football-ball' },
      { name: 'CS2', label: 'Counter-Strike', icon: 'fas fa-crosshairs' },
      { name: 'LOL', label: 'League of Legends', icon: 'fas fa-dragon' },
      { name: 'VALORANT', label: 'Valorant', icon: 'fas fa-crosshairs' }
    ]
  }
]

// Hook to count live games from the odds store
const useLiveGameCount = () => {
  const oddsData = useOddsStore((state) => state.oddsData)

  const liveCount = useMemo(() => {
    if (!oddsData) return 0

    // Get unique canonical_ids where is_live is true
    const liveGames = new Set()
    Object.values(oddsData).forEach((item) => {
      if (item.is_live && item.canonical_id) {
        liveGames.add(item.canonical_id)
      }
    })

    return liveGames.size
  }, [oddsData])

  return { liveCount, loading: false }
}

// Dot matrix ticker messages
const TICKER_MESSAGES = [
  'LIVE ODDS',
  'REAL-TIME UPDATES',
  'BEST LINES',
  'αBET',
  'FIND THE EDGE',
  'SMART BETTING'
]

// Only the Odds module needs Live/Today buttons
const LIVE_TODAY_MODULES = ['Odds']

function SportNav({
  selectedSport,
  onSportSelect,
  collapsed,
  onToggleCollapse,
  onTodayClick,
  onLiveClick,
  isLiveActive,
  isTodayActive,
  selectedModule
}) {
  const [dropdownOpen, setDropdownOpen] = useState(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const buttonRefs = useRef({})
  const { liveCount, loading: liveLoading } = useLiveGameCount()

  // Only show Live/Today for relevant modules
  const showLiveToday = LIVE_TODAY_MODULES.includes(selectedModule)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.sport-dropdown-container') &&
          !event.target.closest('.sport-dropdown-menu')) {
        setDropdownOpen(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSportClick = (sport, event) => {
    if (sport.subLeagues) {
      // Toggle dropdown for sports with sub-leagues
      if (dropdownOpen === sport.name) {
        setDropdownOpen(null)
      } else {
        const button = buttonRefs.current[sport.name]
        if (button) {
          const rect = button.getBoundingClientRect()
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX
          })
        }
        setDropdownOpen(sport.name)
      }
    } else {
      onSportSelect(sport.name)
      setDropdownOpen(null)
    }
  }

  const handleSubLeagueSelect = (subLeague) => {
    onSportSelect(subLeague.name)
    setDropdownOpen(null)
  }

  return (
    <div className={`sport-nav-section ${collapsed ? 'collapsed' : ''}`}>
      {/* Collapse Toggle */}
      <button
        className="sport-nav-toggle"
        onClick={onToggleCollapse}
        title={collapsed ? 'Expand Sports' : 'Collapse Sports'}
      >
        <span className="toggle-label">{selectedSport}</span>

        {/* Dot Matrix Ticker */}
        <div className="dot-matrix-container">
          <div className="dot-matrix-scroll">
            {TICKER_MESSAGES.map((msg, i) => (
              <span key={i}>
                {msg}
                <span className="separator">•</span>
              </span>
            ))}
            {/* Duplicate for seamless loop */}
            {TICKER_MESSAGES.map((msg, i) => (
              <span key={`dup-${i}`}>
                {msg}
                <span className="separator">•</span>
              </span>
            ))}
          </div>
        </div>

        <i className={`fas fa-chevron-${collapsed ? 'down' : 'up'}`}></i>
      </button>

      {/* Sport Navigation */}
      {!collapsed && (
        <nav className="sport-nav">
          {/* Live Button - only for relevant modules */}
          {showLiveToday && (
            <button
              className={`sport-btn live-btn ${isLiveActive ? 'active' : ''}`}
              onClick={onLiveClick}
              title="View live games"
            >
              <i className="fas fa-circle live-pulse"></i>
              <span>Live</span>
              {!liveLoading && liveCount > 0 && (
                <span className="live-count">{liveCount}</span>
              )}
            </button>
          )}

          {/* Today Button - only for relevant modules */}
          {showLiveToday && (
            <button
              className={`sport-btn today-btn ${isTodayActive ? 'active' : ''}`}
              onClick={onTodayClick}
              title="Today's games"
            >
              <i className="fas fa-calendar-day"></i>
              <span>Today</span>
            </button>
          )}

          {/* Sport Buttons */}
          {SPORTS.map((sport) => (
            <div key={sport.name} className="sport-dropdown-container">
              <button
                ref={(el) => buttonRefs.current[sport.name] = el}
                className={`sport-btn ${selectedSport === sport.name ? 'active' : ''} ${sport.subLeagues ? 'has-dropdown' : ''}`}
                onClick={(e) => handleSportClick(sport, e)}
              >
                {sport.logo ? (
                  <img src={sport.logo} alt={sport.name} className="sport-logo" />
                ) : (
                  <i className={sport.icon}></i>
                )}
                <span>{sport.name}</span>
                {sport.subLeagues && (
                  <i className={`fas fa-caret-${dropdownOpen === sport.name ? 'up' : 'down'} dropdown-caret`}></i>
                )}
              </button>
            </div>
          ))}
        </nav>
      )}

      {/* Dropdown Menu (portal-style, fixed position) */}
      {dropdownOpen && (
        <div
          className="sport-dropdown-menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left
          }}
        >
          {SPORTS.find(s => s.name === dropdownOpen)?.subLeagues?.map((sub) => (
            <button
              key={sub.name}
              className={`sport-dropdown-item ${selectedSport === sub.name ? 'active' : ''}`}
              onClick={() => handleSubLeagueSelect(sub)}
            >
              <i className={sub.icon}></i>
              <span>{sub.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default SportNav
